// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useId } from 'react';

import { LabelLegend } from '../../LabelLegend';
import { SegmentedControl } from '../../SegmentedControl';
import { Select } from '../../Select';
import { useSettings } from './SettingsContext';
import {
  GHOST_TRANSPARENCY,
  METRE_SIZE,
  activeLabelSource,
  labelNoteFor,
  labelSourcesFor,
  pickPrimary,
  type ColourKey,
  type Density,
  type GhostKey,
  type SettingsAction,
  type SizeKey,
} from './settingsState';
import styles from './ModelSettings.module.css';

const DENSITY: readonly { value: Density; label: string }[] = [
  { value: 25, label: '25 %' },
  { value: 50, label: '50 %' },
  { value: 100, label: '100 %' },
];
/** Størrelse: the pixel presets, plus Meter (world-sized points on a slider). */
type SizeChoice = SizeKey | 'm';
const SIZE: readonly { value: SizeChoice; label: string; title?: string }[] = [
  { value: 'S', label: 'S' },
  { value: 'M', label: 'M' },
  { value: 'L', label: 'L' },
  { value: 'm', label: 'Meter', title: 'Punkter med en fast størrelse i meter: større tæt på, mindre langt væk' },
];
const GHOST: readonly { value: GhostKey; label: string }[] = [
  { value: 'svag', label: 'Svag' },
  { value: 'normal', label: 'Normal' },
  { value: 'staerk', label: 'Stærk' },
];

const metres3 = (v: number) => v.toLocaleString('da-DK', { minimumFractionDigits: 3, maximumFractionDigits: 3 });

/**
 * Lag › Punktsky's settings (the expander on the Punktsky row): how the whole
 * point layer is drawn — Farver (lens, RGB or a label cloud), Tæthed,
 * Størrelse (pixel presets or metres), Skygge and how see-through the
 * unlabelled points are. Reads and edits the settings through `SettingsContext`.
 */
export function PointCloudSection() {
  const { state, dispatch, facts } = useSettings();
  const set = (patch: Extract<SettingsAction, { type: 'set' }>['patch']) => dispatch({ type: 'set', patch });
  const ghostLabelId = useId();

  const clouds = facts.clouds;
  const labelSources = labelSourcesFor(clouds, pickPrimary(clouds));
  const source = activeLabelSource(labelSources, state.labelSource);
  const labelNote = labelNoteFor(clouds);
  const colourOptions: readonly { value: ColourKey; label: string; disabled?: boolean; title?: string }[] = [
    { value: 'lens', label: 'Linse', title: 'Modusens egne farver: linsen, status, prøver' },
    { value: 'rgb', label: 'RGB', title: 'Scanningens egne farver' },
    {
      value: 'label',
      label: 'Etiket',
      disabled: labelSources.length === 0,
      title: labelSources.length === 0 ? (labelNote ?? undefined) : 'Farv efter en etiketsky (planer, rum, instanser …)',
    },
  ];
  const colour: ColourKey = state.colour === 'label' && !source ? 'lens' : state.colour;
  const sizeChoice: SizeChoice = state.sizeMetres !== null ? 'm' : state.size;

  return (
    <>
      <div className={`${styles.grid} ${styles.gridAligned}`}>
        <span className={styles.label}>Farver</span>
        <SegmentedControl aria-label="Farver" className={styles.seg} items={colourOptions} value={colour} onChange={(c) => set({ colour: c })} />
        {colour === 'label' && source && (
          <>
            <span className={styles.label}>Kilde</span>
            <Select variant="outline" size="sm" aria-label="Etiketsky" value={source.name} onChange={(e) => set({ labelSource: e.target.value })}>
              {labelSources.map((c) => (
                <option key={c.name} value={c.name}>
                  {c.name}
                </option>
              ))}
            </Select>
          </>
        )}
      </div>
      {colour === 'label' && source?.labels && (
        <div className={styles.legendBox}>
          <LabelLegend labels={source.labels} />
        </div>
      )}
      {colour === 'lens' && state.colour === 'label' && labelNote && <p className={styles.note}>{labelNote}</p>}
      <div className={`${styles.grid} ${styles.gridAligned}`}>
        <span className={styles.label}>Tæthed</span>
        <SegmentedControl aria-label="Tæthed" className={styles.seg} items={DENSITY} value={state.density} onChange={(density) => set({ density })} />
        <span className={styles.label}>Størrelse</span>
        <SegmentedControl
          aria-label="Størrelse"
          className={styles.seg}
          items={SIZE}
          value={sizeChoice}
          onChange={(v) => set(v === 'm' ? { sizeMetres: state.sizeMetres ?? METRE_SIZE.initial } : { size: v, sizeMetres: null })}
        />
        {state.sizeMetres !== null && (
          <>
            <span className={styles.label}>Meter</span>
            <span className={styles.slider}>
              <input
                type="range"
                className={styles.range}
                min={METRE_SIZE.min}
                max={METRE_SIZE.max}
                step={METRE_SIZE.step}
                value={state.sizeMetres}
                aria-label="Punktstørrelse i meter"
                onChange={(e) => set({ sizeMetres: Number(e.target.value) })}
              />
              <span className={styles.sliderValue}>{metres3(state.sizeMetres)} m</span>
            </span>
          </>
        )}
        <span className={styles.label}>Skygge</span>
        <SegmentedControl aria-label="Skygge" className={styles.seg} items={GHOST} value={state.ghost} onChange={(ghost) => set({ ghost })} />
        <span className={`${styles.label} ${styles.wide}`} id={ghostLabelId}>
          Gennemsigtighed for umærkede punkter
        </span>
        <span className={`${styles.slider} ${styles.wide}`}>
          <input
            type="range"
            className={styles.range}
            min={GHOST_TRANSPARENCY.min}
            max={GHOST_TRANSPARENCY.max}
            step={GHOST_TRANSPARENCY.step}
            value={state.ghostTransparency}
            aria-labelledby={ghostLabelId}
            disabled={colour !== 'lens'}
            title={colour !== 'lens' ? 'Kun med Linse — i RGB og Etiket er ingen punkter umærkede' : undefined}
            onChange={(e) => set({ ghostTransparency: Number(e.target.value) })}
          />
          <span className={styles.sliderValue}>{Math.round(state.ghostTransparency * 100)} %</span>
        </span>
      </div>
    </>
  );
}
